import { UpdateUsedQuantity } from '@/src/types/apps/gygIngredientsUsedQuantity'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Redux } from '../..'
import {
  RESTO365_INGREDIENTS_USED_QUANTITY,
  RESTO365_INGREDIENTS_USED_QUANTITY_CEREBRO_PRODUCTS,
  RESTO365_INGREDIENTS_USED_QUANTITY_CREATE,
  RESTO365_INGREDIENTS_USED_QUANTITY_DELETE,
  RESTO365_INGREDIENTS_USED_QUANTITY_LIST,
  RESTO365_INGREDIENTS_USED_QUANTITY_RESTORE,
  RESTO365_INGREDIENTS_USED_QUANTITY_UPDATE
} from './action'
import { QueryParams } from '@/src/types/apps/queryParams'

interface IngredientsUsedQuantity {
  nutritionalUsedQuantityData: UpdateUsedQuantity[]
  success?: string | null
  error?: string | null
  loading?: boolean
  cerebroProducts?: any[]
  meta?: {
    totalRows: number
    pages: number
  }
}

const initialState: IngredientsUsedQuantity = {
  nutritionalUsedQuantityData: [],
  success: null,
  error: null,
  loading: false,
  cerebroProducts: [],
  meta: {
    totalRows: 0,
    pages: 0
  }
}

interface IngredientsUsedQuantityResponse {
  data: UpdateUsedQuantity[]
  meta?: {
    totalRows: number
    pages: number
  }
}

export const getCerebroProducts = createAsyncThunk<any, QueryParams, Redux>(
  RESTO365_INGREDIENTS_USED_QUANTITY_CEREBRO_PRODUCTS,
  async (params, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.get('cerebro-products', { params })

      return response.data
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to fetch cerebro products')
    }
  }
)

export const getIngredientsUsedQuantity = createAsyncThunk<IngredientsUsedQuantityResponse, any, Redux>(
  RESTO365_INGREDIENTS_USED_QUANTITY_LIST,
  async (params, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient('ingredients-used-quantity', { params })
      const data = responseData.data as IngredientsUsedQuantityResponse

      return data
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || 'Failed to fetch ingredients nutritional info quantity used'
      )
    }
  }
)

export const createIngredientsUsedQuantity = createAsyncThunk<UpdateUsedQuantity, any, Redux>(
  RESTO365_INGREDIENTS_USED_QUANTITY_CREATE,
  async (nutritionalInfoData, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.post('ingredients-used-quantity', nutritionalInfoData)
      const data = response.data

      return data
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to create nutritional info quantity used')
    }
  }
)

export const updateIngredientsUsedQuantity = createAsyncThunk<UpdateUsedQuantity, UpdateUsedQuantity, Redux>(
  RESTO365_INGREDIENTS_USED_QUANTITY_UPDATE,
  async (nutritionalInfoData, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.patch(
        `ingredients-used-quantity/${nutritionalInfoData.id}`,
        nutritionalInfoData
      )
      const data = response.data

      return data
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to update nutritional info quantity used')
    }
  }
)

export const deleteIngredientsUsedQuantity = createAsyncThunk<string, string, Redux>(
  RESTO365_INGREDIENTS_USED_QUANTITY_DELETE,
  async (id, { extra, rejectWithValue }) => {
    try {
      await extra.httpClient.delete(`ingredients-used-quantity/${id}`)

      return id
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to delete nutritional info quantity used')
    }
  }
)

export const restoreIngredientsUsedQuantity = createAsyncThunk<UpdateUsedQuantity, string, Redux>(
  RESTO365_INGREDIENTS_USED_QUANTITY_RESTORE,
  async (id, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.patch(`ingredients-used-quantity/${id}/restore`)
      const data = response.data

      return data
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to restore nutritional info quantity used')
    }
  }
)

export const gygIngredientsUsedQuantity = createSlice({
  name: RESTO365_INGREDIENTS_USED_QUANTITY,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      /**
       * Fetch Cerebro Products
       */
      .addCase(getCerebroProducts.fulfilled, (state, action) => {
        state.cerebroProducts = action.payload
        state.loading = false
        state.error = null
      })
      .addCase(getCerebroProducts.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch cerebro products'
        state.cerebroProducts = []
      })
      .addCase(getCerebroProducts.pending, state => {
        state.loading = true
        state.error = null
        state.cerebroProducts = []
      })

      /**
       * Fetch Ingredients nutritional Info List
       */
      .addCase(getIngredientsUsedQuantity.fulfilled, (state, action) => {
        state.nutritionalUsedQuantityData = action.payload.data
        state.meta = action.payload.meta || { totalRows: 0, pages: 0 }
        state.loading = false
        state.error = null
      })
      .addCase(getIngredientsUsedQuantity.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch ingredients nutritional info quantity used'
        state.success = null
      })
      .addCase(getIngredientsUsedQuantity.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })

      /**
       * Create Ingredients Nutritional Info quantity used
       */
      .addCase(createIngredientsUsedQuantity.fulfilled, state => {
        state.success = 'Nutritional info quantity used created successfully'
        state.loading = false
        state.error = null
      })
      .addCase(createIngredientsUsedQuantity.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) || 'Failed to create nutritional info'
        state.success = null
      })
      .addCase(createIngredientsUsedQuantity.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })

      /**
       * Update Ingredients Nutritional Info quantity used
       */
      .addCase(updateIngredientsUsedQuantity.fulfilled, state => {
        state.success = 'Nutritional info quantity used updated successfully'
        state.loading = false
        state.error = null
      })
      .addCase(updateIngredientsUsedQuantity.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) || 'Failed to update nutritional info quantity used'
        state.success = null
      })
      .addCase(updateIngredientsUsedQuantity.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })

      /**
       * Delete Ingredients Nutritional info quantity used
       */
      .addCase(deleteIngredientsUsedQuantity.fulfilled, (state, action) => {
        state.nutritionalUsedQuantityData = state.nutritionalUsedQuantityData.map(item =>
          String(item.id) === String(action.payload) ? { ...item, isDeleted: true } : item
        )
        state.success = 'Nutritional info quantity used deleted successfully'
        state.loading = false
        state.error = null
      })
      .addCase(deleteIngredientsUsedQuantity.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) || 'Failed to delete nutritional info quantity used'
        state.success = null
      })
      .addCase(deleteIngredientsUsedQuantity.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })

      /**
       * Restore Ingredients Nutritional Info quantity used
       */
      .addCase(restoreIngredientsUsedQuantity.fulfilled, (state, action) => {
        state.nutritionalUsedQuantityData = state.nutritionalUsedQuantityData.map(item =>
          String(item.id) === String(action.payload) ? { ...item, isDeleted: false } : item
        )
        state.success = 'Nutritional info quantity used restored successfully'
        state.loading = false
        state.error = null
      })
      .addCase(restoreIngredientsUsedQuantity.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) || 'Failed to restore nutritional info quantity used'
        state.success = null
      })
      .addCase(restoreIngredientsUsedQuantity.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
  }
})

export default gygIngredientsUsedQuantity.reducer
